import { supabase } from '../lib/supabase';
import { User, Tenant, UserInvitation } from '../types/user';
import { Conversation, Message } from '../types/ai';

// Fallback local para conversas
const getLocalConversations = (userId: string, tenantId: string) => {
  const key = `conversations_${userId}_${tenantId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : [];
};

const saveLocalConversations = (userId: string, tenantId: string, conversations: any[]) => {
  const key = `conversations_${userId}_${tenantId}`;
  localStorage.setItem(key, JSON.stringify(conversations));
};

const getLocalMessages = (conversationId: string) => {
  const key = `messages_${conversationId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : [];
};

const saveLocalMessages = (conversationId: string, messages: any[]) => {
  const key = `messages_${conversationId}`;
  localStorage.setItem(key, JSON.stringify(messages));
};

// Serviço de autenticação
export const authService = {
  // Login do usuário (usa Supabase Auth)
  async login(email: string, password: string) {
    try {
      console.log('🔐 [1/4] Iniciando login com Supabase Auth...');
      
      // Fazer login com Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        console.error('❌ Erro no Supabase Auth:', authError);
        throw new Error(authError.message || 'Email ou senha incorretos');
      }

      if (!authData.user) {
        throw new Error('Usuário não encontrado');
      }

      console.log('✅ [2/4] Autenticação bem-sucedida! User ID:', authData.user.id);

      // Aguardar um pouco para garantir que o trigger criou o perfil
      await new Promise(resolve => setTimeout(resolve, 500));

      // Buscar dados do usuário na tabela customizada (com retry)
      let user = null;
      let userError = null;
      
      for (let i = 0; i < 3; i++) {
        const result = await supabase
          .from('users')
          .select('*')
          .eq('id', authData.user.id)
          .single();
        
        user = result.data;
        userError = result.error;
        
        if (user) break;
        
        console.log(`⏳ Tentativa ${i + 1}/3: Aguardando perfil ser criado...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (userError || !user) {
        console.error('❌ Usuário não encontrado na tabela após 3 tentativas:', userError);
        throw new Error('Perfil de usuário não encontrado. Tente fazer login novamente.');
      }

      console.log('✅ [3/4] Perfil de usuário encontrado!');

      // Buscar tenant do usuário
      const { data: tenant, error: tenantError} = await supabase
        .from('tenants')
        .select('*')
        .eq('id', user.tenant_id)
        .single();

      if (tenantError || !tenant) {
        console.error('❌ Tenant não encontrado:', tenantError);
        throw new Error('Empresa não encontrada');
      }

      console.log('✅ [4/4] Tenant encontrado! Login completo.');

      // Atualizar último login
      await supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', user.id);

      // Converter dados do Supabase para o formato da aplicação
      const formattedUser: User = {
        id: user.id,
        name: user.name,
        email: user.email,
        tenantId: user.tenant_id,
        role: user.role as 'user' | 'manager',
        status: user.status as 'active' | 'inactive',
        createdAt: new Date(user.created_at),
        lastLogin: user.last_login ? new Date(user.last_login) : undefined,
        twoFactorEnabled: user.two_factor_enabled,
        invitedBy: user.invited_by,
        invitationAcceptedAt: user.invitation_accepted_at ? new Date(user.invitation_accepted_at) : undefined
      };

      const formattedTenant: Tenant = {
        id: tenant.id,
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status as 'active' | 'suspended' | 'cancelled',
        createdAt: new Date(tenant.created_at),
        settings: tenant.settings || { enabledAIs: ['openai', 'anthropic', 'google'] },
        billing: tenant.billing,
        contact: tenant.contact,
        accountType: tenant.account_type || 'personal',
        isPersonal: tenant.account_type === 'personal' || !tenant.account_type,
        maxUsers: tenant.max_users || 1,
        features: tenant.features || {},
        enterpriseFeatures: tenant.enterprise_features ? {
          aiConfiguration: tenant.enterprise_features.ai_configuration || false,
          securitySettings: tenant.enterprise_features.security_settings || false,
          contentRestrictions: tenant.enterprise_features.content_restrictions || false,
          customBranding: tenant.enterprise_features.custom_branding || false,
          sso: tenant.enterprise_features.sso || false,
          advancedAnalytics: tenant.enterprise_features.advanced_analytics || false,
        } : undefined
      };

      return { user: formattedUser, tenant: formattedTenant };
    } catch (error) {
      console.error('❌ Erro completo no login:', error);
      throw error;
    }
  },


  // Login com Google
  async loginWithGoogle() {
    try {
      console.log('🔐 Iniciando login com Google via Supabase Auth...');
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error('❌ Erro ao iniciar login com Google:', error);
        throw error;
      }

      console.log('✅ Redirecionando para Google...');
      // O Supabase vai redirecionar automaticamente
      return data;
      
    } catch (error) {
      console.error('❌ Erro no login com Google:', error);
      throw error;
    }
  },

  // Registro de usuário SIMPLES (sem onboarding - onboarding será feito depois dentro do dashboard)
  async register(data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
  }) {
    try {
      console.log('📝 [1/2] Criando conta no Supabase Auth...');
      console.log('Dados do registro:', {
        email: data.email,
        name: data.name,
        phone: data.phone
      });
      
      // Criar usuário no Supabase Auth com metadata básica
      // O trigger handle_new_user() vai criar tenant e user automaticamente
      console.log('⏳ Chamando supabase.auth.signUp()...');
      
      const signUpPayload = {
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: undefined,
          data: {
            name: data.name,
            phone: data.phone || '',
            account_type: 'personal', // Sempre pessoal inicialmente (pode mudar no onboarding)
            onboarding_completed: false // Flag para indicar que precisa fazer onboarding
          }
        }
      };
      
      console.log('Payload do signUp:', signUpPayload);
      
      const { data: authData, error: authError } = await supabase.auth.signUp(signUpPayload);

      console.log('Resposta do signUp:', { authData, authError });

      // Se o usuário já existe no auth mas não tem perfil, buscar o usuário existente
      if (authError && authError.status === 422 && authError.code === 'user_already_exists') {
        console.log('⚠️ Usuário já existe no auth.users, tentando buscar...');
        
        // Fazer login para obter o usuário
        const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password
        });

        if (loginError || !loginData.user) {
          throw new Error('Usuário já existe mas não foi possível fazer login. Tente fazer login na tela anterior.');
        }

        console.log('✅ Usuário encontrado no auth! ID:', loginData.user.id);

        // Verificar se já tem perfil
        const { data: existingUser } = await supabase
          .from('users')
          .select('*')
          .eq('id', loginData.user.id)
          .single();

        if (existingUser) {
          throw new Error('Usuário já cadastrado. Por favor, faça login.');
        }

        console.log('⚠️ Usuário órfão detectado! Criando perfil...');
        authData.user = loginData.user;
      } else if (authError) {
        console.error('❌ Erro ao criar conta no Supabase Auth:', authError);
        console.error('Detalhes do erro:', {
          message: authError.message,
          status: authError.status,
          code: authError.code,
          name: authError.name
        });
        throw new Error(authError.message || 'Erro ao criar conta');
      }

      if (!authData.user) {
        throw new Error('Erro ao criar usuário');
      }

      console.log('✅ [2/2] Usuário criado no Supabase Auth! User ID:', authData.user.id);
      console.log('🔍 O trigger handle_new_user() vai criar tenant e perfil automaticamente...');
      
      // Aguardar o trigger handle_new_user() criar tenant e user
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Buscar tenant e user criados pelo trigger
      console.log('🔍 Buscando tenant e usuário criados pelo trigger...');
      
      let retries = 3;
      let finalUser = null;
      let finalTenant = null;

      for (let i = 0; i < retries; i++) {
        const { data: existingUser, error: userFetchError } = await supabase
          .from('users')
          .select('*, tenants(*)')
          .eq('id', authData.user.id)
          .single();

        if (existingUser && existingUser.tenants) {
          console.log('✅ Trigger criou o usuário e tenant com sucesso!');
          finalUser = existingUser;
          finalTenant = existingUser.tenants;
          break;
        }

        console.log(`⏳ Tentativa ${i + 1}/${retries}: Aguardando trigger...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (!finalUser || !finalTenant) {
        console.warn('⚠️ Trigger não criou o usuário, criando manualmente...');
        
        // Criar tenant manualmente
        const tenantName = `${data.name} - Conta Pessoal`;
        const tenantDomain = `user-${authData.user.id}-${Date.now()}.internal`;

        let createdTenant;
        const { data: newTenant, error: tenantError } = await supabase
          .from('tenants')
          .insert({
            name: tenantName,
            domain: tenantDomain,
            status: 'active',
            plan: 'free'
          })
          .select()
          .single();

        if (tenantError) {
          console.error('❌ Erro ao criar tenant:', tenantError);
          // Tentar buscar tenant existente
          const { data: existingTenant } = await supabase
            .from('tenants')
            .select('*')
            .eq('domain', tenantDomain)
            .single();
          
          if (!existingTenant) {
            throw new Error(`Erro ao criar empresa: ${tenantError?.message}`);
          }
          
          console.log('✅ Tenant já existe, usando existente:', existingTenant.id);
          createdTenant = existingTenant;
        } else {
          console.log('✅ Tenant criado:', newTenant.id);
          createdTenant = newTenant;
        }

        // Verificar se o usuário já existe antes de criar
        const { data: existingUserCheck } = await supabase
          .from('users')
          .select('*')
          .eq('id', authData.user.id)
          .single();

        if (existingUserCheck) {
          console.log('✅ Usuário já existe, usando existente:', existingUserCheck.id);
          finalUser = existingUserCheck;
          finalTenant = createdTenant;
        } else {
          // Criar usuário manualmente
          const { data: createdUser, error: userError } = await supabase
            .from('users')
            .insert({
              id: authData.user.id,
              name: data.name,
              email: data.email,
              phone: data.phone || null,
              tenant_id: createdTenant.id,
              role: 'owner',
              status: 'active'
            })
            .select()
            .single();

          if (userError) {
            console.error('❌ Erro ao criar usuário:', userError);
            
            // Se der erro de duplicate key, buscar o usuário existente
            if (userError.code === '23505') {
              console.log('⚠️ Usuário já existe (duplicate key), buscando...');
              const { data: existingUser } = await supabase
                .from('users')
                .select('*')
                .eq('id', authData.user.id)
                .single();
              
              if (existingUser) {
                console.log('✅ Usuário encontrado:', existingUser.id);
                finalUser = existingUser;
                finalTenant = createdTenant;
              } else {
                throw new Error(`Erro ao criar perfil: ${userError?.message}`);
              }
            } else {
              throw new Error(`Erro ao criar perfil: ${userError?.message}`);
            }
          } else {
            finalUser = createdUser;
            finalTenant = createdTenant;
          }
        }
      }

      const user = finalUser;
      const tenant = finalTenant;

      console.log('✅ Perfil e tenant criados com sucesso!');

      // Converter dados do Supabase para o formato da aplicação
      const formattedUser: User = {
        id: user.id,
        name: user.name,
        email: user.email,
        tenantId: user.tenant_id,
        role: user.role as 'user' | 'manager',
        status: user.status as 'active' | 'inactive',
        createdAt: new Date(user.created_at),
        twoFactorEnabled: user.two_factor_enabled,
        invitedBy: user.invited_by,
        invitationAcceptedAt: user.invitation_accepted_at ? new Date(user.invitation_accepted_at) : undefined
      };

      const formattedTenant: Tenant = {
        id: tenant.id,
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status as 'active' | 'suspended' | 'cancelled',
        createdAt: new Date(tenant.created_at),
        settings: tenant.settings || { enabledAIs: ['openai', 'anthropic', 'google'] },
        billing: tenant.billing,
        contact: tenant.contact,
        accountType: tenant.account_type || 'personal',
        isPersonal: tenant.account_type === 'personal' || !tenant.account_type,
        maxUsers: tenant.max_users || 1,
        features: tenant.features || {},
        enterpriseFeatures: tenant.enterprise_features ? {
          aiConfiguration: tenant.enterprise_features.ai_configuration || false,
          securitySettings: tenant.enterprise_features.security_settings || false,
          contentRestrictions: tenant.enterprise_features.content_restrictions || false,
          customBranding: tenant.enterprise_features.custom_branding || false,
          sso: tenant.enterprise_features.sso || false,
          advancedAnalytics: tenant.enterprise_features.advanced_analytics || false,
        } : undefined
      };

      return { user: formattedUser, tenant: formattedTenant };
    } catch (error) {
      console.error('❌ Erro completo no registro:', error);
      throw error;
    }
  },

  async getUserById(userId: string) {
    try {
      console.log('🔍 Buscando usuário por ID:', userId);
      
      const { data: user, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Erro ao buscar usuário:', error);
        throw error;
      }

      if (!user) {
        throw new Error('Usuário não encontrado');
      }

      return {
        id: user.id,
        name: user.name,
        email: user.email,
        tenantId: user.tenant_id,
        role: user.role as 'owner' | 'manager' | 'user',
        status: user.status as 'active' | 'invited' | 'inactive',
        createdAt: new Date(user.created_at),
        phone: user.phone,
        invitedBy: user.invited_by,
        invitationAcceptedAt: user.invitation_accepted_at ? new Date(user.invitation_accepted_at) : undefined
      };
    } catch (error) {
      console.error('❌ Erro ao buscar usuário por ID:', error);
      throw error;
    }
  }
};

// Serviço de gestão de usuários
export const userService = {
  // Buscar usuários de um tenant
  async getTenantUsers(tenantId: string) {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select(`
          *,
          invited_by_user:users!users_invited_by_fkey(name, email)
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error('Erro ao buscar usuários');
      }

      return users?.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        tenantId: user.tenant_id,
        role: user.role as 'user' | 'manager',
        status: user.status as 'active' | 'inactive',
        createdAt: new Date(user.created_at),
        lastLogin: user.last_login ? new Date(user.last_login) : undefined,
        twoFactorEnabled: user.two_factor_enabled,
        invitedBy: user.invited_by,
        invitedByName: user.invited_by_user?.name,
        invitationAcceptedAt: user.invitation_accepted_at ? new Date(user.invitation_accepted_at) : undefined
      })) || [];
    } catch (error) {
      throw error;
    }
  },

  // Atualizar usuário
  async updateUser(userId: string, updates: Partial<User>) {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: updates.name,
          role: updates.role,
          status: updates.status,
          two_factor_enabled: updates.twoFactorEnabled
        })
        .eq('id', userId);

      if (error) {
        throw new Error('Erro ao atualizar usuário');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Suspender/reativar usuário
  async toggleUserStatus(userId: string, status: 'active' | 'inactive') {
    try {
      const { error } = await supabase
        .from('users')
        .update({ status })
        .eq('id', userId);

      if (error) {
        throw new Error('Erro ao alterar status do usuário');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Deletar usuário
  async deleteUser(userId: string) {
    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (error) {
        throw new Error('Erro ao deletar usuário');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Buscar estatísticas de usuários
  async getUserStats(tenantId: string) {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('role, status, created_at')
        .eq('tenant_id', tenantId);

      if (error) {
        throw new Error('Erro ao buscar estatísticas');
      }

      const totalUsers = users?.length || 0;
      const activeUsers = users?.filter(u => u.status === 'active').length || 0;
      const managers = users?.filter(u => u.role === 'manager').length || 0;
      const regularUsers = users?.filter(u => u.role === 'user').length || 0;

      // Usuários criados nos últimos 30 dias
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentUsers = users?.filter(u => new Date(u.created_at) > thirtyDaysAgo).length || 0;

      return {
        totalUsers,
        activeUsers,
        managers,
        regularUsers,
        recentUsers
      };
    } catch (error) {
      throw error;
    }
  }
};

// Serviço de convites
export const invitationService = {
  // Criar convite
  async createInvitation(tenantId: string, email: string, role: 'user' | 'manager', invitedBy: string) {
    try {
      // Verificar se usuário já existe
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .eq('tenant_id', tenantId)
        .single();

      if (existingUser) {
        throw new Error('Usuário já existe nesta empresa');
      }

      // Verificar se já existe convite pendente
      const { data: existingInvitation } = await supabase
        .from('user_invitations')
        .select('id')
        .eq('email', email)
        .eq('tenant_id', tenantId)
        .eq('status', 'pending')
        .single();

      if (existingInvitation) {
        throw new Error('Já existe um convite pendente para este email');
      }

      // Gerar token de convite
      const token = await supabase.rpc('generate_invitation_token');
      
      // Definir data de expiração (7 dias)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Criar convite
      const { data: invitation, error } = await supabase
        .from('user_invitations')
        .insert({
          tenant_id: tenantId,
          email,
          role,
          invited_by: invitedBy,
          expires_at: expiresAt.toISOString(),
          token: token.data
        })
        .select()
        .single();

      if (error || !invitation) {
        throw new Error('Erro ao criar convite');
      }

      return {
        id: invitation.id,
        tenantId: invitation.tenant_id,
        email: invitation.email,
        role: invitation.role as 'user' | 'manager',
        invitedBy: invitation.invited_by,
        invitedAt: new Date(invitation.invited_at),
        expiresAt: new Date(invitation.expires_at),
        token: invitation.token,
        status: invitation.status as 'pending' | 'accepted' | 'expired' | 'cancelled'
      } as UserInvitation;
    } catch (error) {
      throw error;
    }
  },

  // Buscar convites de um tenant
  async getTenantInvitations(tenantId: string) {
    try {
      const { data: invitations, error } = await supabase
        .from('user_invitations')
        .select(`
          *,
          invited_by_user:users!user_invitations_invited_by_fkey(name, email)
        `)
        .eq('tenant_id', tenantId)
        .order('invited_at', { ascending: false });

      if (error) {
        throw new Error('Erro ao buscar convites');
      }

      return invitations?.map(invitation => ({
        id: invitation.id,
        tenantId: invitation.tenant_id,
        email: invitation.email,
        role: invitation.role as 'user' | 'manager',
        invitedBy: invitation.invited_by,
        invitedByName: invitation.invited_by_user?.name,
        invitedAt: new Date(invitation.invited_at),
        expiresAt: new Date(invitation.expires_at),
        token: invitation.token,
        status: invitation.status as 'pending' | 'accepted' | 'expired' | 'cancelled',
        acceptedAt: invitation.accepted_at ? new Date(invitation.accepted_at) : undefined,
        acceptedBy: invitation.accepted_by
      })) || [];
    } catch (error) {
      throw error;
    }
  },

  // Cancelar convite
  async cancelInvitation(invitationId: string) {
    try {
      const { error } = await supabase
        .from('user_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) {
        throw new Error('Erro ao cancelar convite');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Reenviar convite
  async resendInvitation(invitationId: string) {
    try {
      // Buscar convite atual
      const { data: invitation, error: fetchError } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (fetchError || !invitation) {
        throw new Error('Convite não encontrado');
      }

      // Gerar novo token e data de expiração
      const token = await supabase.rpc('generate_invitation_token');
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Atualizar convite
      const { error } = await supabase
        .from('user_invitations')
        .update({
          token: token.data,
          expires_at: expiresAt.toISOString(),
          status: 'pending'
        })
        .eq('id', invitationId);

      if (error) {
        throw new Error('Erro ao reenviar convite');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Aceitar convite (para registro de usuário)
  async acceptInvitation(token: string, name: string, password: string) {
    try {
      console.log('📧 Aceitando convite com token:', token);

      // 1. Buscar convite pelo token
      const { data: invitation, error: fetchError } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('token', token)
        .eq('status', 'pending')
        .single();

      if (fetchError || !invitation) {
        throw new Error('Convite inválido ou expirado');
      }

      // 2. Verificar se não expirou
      if (new Date(invitation.expires_at) < new Date()) {
        throw new Error('Convite expirado');
      }

      console.log('✅ Convite válido para:', invitation.email, 'tenant:', invitation.tenant_id);

      // 3. Criar usuário no Supabase Auth (não cria sessão pois é via convite público)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: {
            name,
            full_name: name,
            skip_tenant_creation: true // Flag para o trigger saber que não deve criar tenant
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Erro ao criar usuário no Auth');

      console.log('✅ Usuário criado no Auth:', authData.user.id);

      // 4. Aguardar trigger processar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 5. Deletar o tenant criado automaticamente pelo trigger (se existir)
      const { data: autoTenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('domain', `${invitation.email}.internal`)
        .single();

      if (autoTenant) {
        console.log('🗑️ Deletando tenant automático criado pelo trigger');
        await supabase
          .from('tenants')
          .delete()
          .eq('id', autoTenant.id);
      }

      // 6. Atualizar usuário na tabela customizada com os dados corretos
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name,
          tenant_id: invitation.tenant_id,
          role: invitation.role,
          status: 'active',
          invited_by: invitation.invited_by,
          invitation_accepted_at: new Date().toISOString()
        })
        .eq('id', authData.user.id);

      if (updateError) {
        console.error('❌ Erro ao atualizar usuário:', updateError);
        throw new Error('Erro ao vincular usuário ao tenant');
      }

      // 7. Buscar usuário atualizado
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userError || !newUser) {
        throw new Error('Erro ao buscar usuário criado');
      }

      console.log('✅ Usuário vinculado ao tenant:', newUser.tenant_id);

      // 8. Marcar convite como aceito
      const { error: inviteUpdateError } = await supabase
        .from('user_invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          accepted_by: newUser.id
        })
        .eq('id', invitation.id);

      if (inviteUpdateError) {
        console.error('⚠️ Erro ao atualizar convite:', inviteUpdateError);
      }

      console.log('🎉 Convite aceito com sucesso!');

      return {
        id: newUser.id,
        name: newUser.name,
        email: newUser.email,
        tenantId: newUser.tenant_id,
        role: newUser.role as 'user' | 'manager',
        status: newUser.status as 'active' | 'inactive',
        createdAt: new Date(newUser.created_at),
        invitedBy: newUser.invited_by,
        invitationAcceptedAt: new Date(newUser.invitation_accepted_at)
      } as User;
    } catch (error) {
      throw error;
    }
  },

  // Verificar convite válido
  async validateInvitation(token: string) {
    try {
      const { data: invitation, error } = await supabase
        .from('user_invitations')
        .select(`
          *,
          tenant:tenants(name, domain)
        `)
        .eq('token', token)
        .eq('status', 'pending')
        .single();

      if (error || !invitation) {
        return null;
      }

      // Verificar se não expirou
      if (new Date(invitation.expires_at) < new Date()) {
        return null;
      }

      return {
        id: invitation.id,
        email: invitation.email,
        role: invitation.role as 'user' | 'manager',
        tenantName: invitation.tenant?.name,
        tenantDomain: invitation.tenant?.domain,
        expiresAt: new Date(invitation.expires_at)
      };
    } catch (error) {
      return null;
    }
  }
};

// Serviço de tenants
export const tenantService = {
  // Atualizar configurações do tenant
  async updateTenantSettings(tenantId: string, settings: any) {
    try {
      const { error } = await supabase
        .from('tenants')
        .update({ settings })
        .eq('id', tenantId);

      if (error) {
        throw new Error('Erro ao atualizar configurações');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Buscar tenant por ID
  async getTenantById(tenantId: string) {
    try {
      const { data: tenant, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();

      if (error || !tenant) {
        throw new Error('Tenant não encontrado');
      }

      return {
        id: tenant.id,
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status as 'active' | 'suspended' | 'cancelled',
        createdAt: new Date(tenant.created_at),
        settings: tenant.settings || { enabledAIs: ['openai', 'anthropic', 'google'] },
        billing: tenant.billing,
        contact: tenant.contact,
        accountType: tenant.account_type || 'personal',
        isPersonal: tenant.account_type === 'personal' || !tenant.account_type,
        maxUsers: tenant.max_users || 1,
        features: tenant.features || {}
      } as Tenant;
    } catch (error) {
      throw error;
    }
  }
};

// Serviço de conversas
export const conversationService = {
  // Buscar conversas do usuário
  async getUserConversations(userId: string, tenantId: string) {
    try {
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .order('updated_at', { ascending: false });

      if (error) {
        throw new Error('Erro ao buscar conversas');
      }

      return conversations?.map(conv => ({
        id: conv.id,
        aiId: conv.ai_id,
        modelId: conv.model_id,
        title: conv.title,
        createdAt: new Date(conv.created_at),
        messageCount: conv.message_count || 0, // ⭐ Contador de mensagens
        messages: [] // Será carregado separadamente
      })) || [];
    } catch (error) {
      throw error;
    }
  },

  // Criar nova conversa
  async createConversation(userId: string, tenantId: string, aiId: string, modelId: string, title: string) {
    try {
      const { data: conversation, error } = await supabase
        .from('conversations')
        .insert({
          user_id: userId,
          tenant_id: tenantId,
          ai_id: aiId,
          model_id: modelId,
          title
        })
        .select()
        .single();

      if (error || !conversation) {
        throw new Error('Erro ao criar conversa');
      }

      return {
        id: conversation.id,
        aiId: conversation.ai_id,
        modelId: conversation.model_id,
        title: conversation.title,
        createdAt: new Date(conversation.created_at),
        messageCount: conversation.message_count || 0,
        messages: []
      };
    } catch (error) {
      throw error;
    }
  },

  // Buscar mensagens de uma conversa
  async getConversationMessages(conversationId: string) {
    try {
      const { data: messages, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        throw new Error('Erro ao buscar mensagens');
      }

      return messages?.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role as 'user' | 'assistant',
        timestamp: new Date(msg.created_at),
        modelId: msg.model_id,
        modelName: msg.model_name,
        attachments: msg.attachments || undefined
      })) || [];
    } catch (error) {
      throw error;
    }
  },

  // Adicionar mensagem
  async addMessage(
    conversationId: string, 
    content: string, 
    role: 'user' | 'assistant', 
    modelId?: string, 
    modelName?: string,
    attachments?: Array<{ name: string; size: number; type: string; file?: File }>,
    tokens?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number },
    aiProvider?: string
  ) {
    try {
      // Converter attachments para formato serializável (sem File objects)
      const serializedAttachments = attachments?.map(att => ({
        name: att.name,
        size: att.size,
        type: att.type
        // TODO: Implementar upload de arquivo e adicionar URL aqui
      }));

      // Calcular custo se tokens estiverem disponíveis (silenciosamente)
      let cost = 0;
      if (tokens?.prompt_tokens && tokens?.completion_tokens && modelId) {
        const { calculateMessageCost } = await import('./cost-calculator');
        cost = calculateMessageCost(
          modelId,
          tokens.prompt_tokens,
          tokens.completion_tokens,
          aiProvider
        );
      }

      const { data: message, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          content,
          role,
          model_id: modelId,
          model_name: modelName,
          attachments: serializedAttachments || null,
          prompt_tokens: tokens?.prompt_tokens || 0,
          completion_tokens: tokens?.completion_tokens || 0,
          total_tokens: tokens?.total_tokens || 0,
          cost: cost
        })
        .select()
        .single();

      if (error || !message) {
        console.error('Erro ao adicionar mensagem:', error);
        throw new Error('Erro ao adicionar mensagem');
      }

      // Retornar mensagem com attachments incluindo File objects temporários para preview
      const messageWithFiles = {
        id: message.id,
        content: message.content,
        role: message.role as 'user' | 'assistant',
        timestamp: new Date(message.created_at),
        modelId: message.model_id,
        modelName: message.model_name,
        attachments: attachments || undefined
      };

      return messageWithFiles;
    } catch (error) {
      console.error('Erro ao adicionar mensagem:', error);
      throw error;
    }
  },

  // Atualizar título da conversa
  async updateConversationTitle(conversationId: string, title: string) {
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ title, updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      if (error) {
        throw new Error('Erro ao atualizar título da conversa');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Atualizar IA da conversa
  async updateConversationAI(conversationId: string, aiId: string, modelId: string) {
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ 
          ai_id: aiId, 
          model_id: modelId, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', conversationId);

      if (error) {
        throw new Error('Erro ao atualizar IA da conversa');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Excluir conversa
  async deleteConversation(conversationId: string) {
    try {
      // Primeiro, excluir todas as mensagens da conversa
      const { error: messagesError } = await supabase
        .from('messages')
        .delete()
        .eq('conversation_id', conversationId);

      if (messagesError) {
        throw new Error('Erro ao excluir mensagens da conversa');
      }

      // Depois, excluir a conversa
      const { error: conversationError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (conversationError) {
        throw new Error('Erro ao excluir conversa');
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Limpar conversas vazias (sem mensagens)
  async cleanupEmptyConversations(userId: string, tenantId: string) {
    try {
      // Buscar conversas que não têm mensagens
      const { data: emptyConversations, error: selectError } = await supabase
        .from('conversations')
        .select(`
          id,
          messages!left (id)
        `)
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (selectError) {
        throw new Error('Erro ao buscar conversas vazias');
      }

      // Filtrar conversas que realmente não têm mensagens
      const conversationsToDelete = emptyConversations?.filter(conv => 
        !conv.messages || conv.messages.length === 0
      ).map(conv => conv.id) || [];

      if (conversationsToDelete.length > 0) {
        // Excluir conversas vazias
        const { error: deleteError } = await supabase
          .from('conversations')
          .delete()
          .in('id', conversationsToDelete);

        if (deleteError) {
          throw new Error('Erro ao excluir conversas vazias');
        }

        console.log(`🧹 Limpeza: ${conversationsToDelete.length} conversas vazias removidas`);
        return conversationsToDelete.length;
      }

      return 0;
    } catch (error) {
      console.error('Erro na limpeza de conversas vazias:', error);
      throw error;
    }
  },

  // ==========================================
  // SOFT DELETE DE MENSAGENS (SUPER ADMIN ONLY)
  // ==========================================

  /**
   * Fazer soft delete de uma mensagem (apenas super admin)
   * @param messageId ID da mensagem
   * @param adminUserId ID do super admin que está excluindo
   * @param messageType Tipo de mensagem ('regular' ou 'assistant')
   */
  async softDeleteMessage(messageId: string, adminUserId: string, messageType: 'regular' | 'assistant' = 'regular'): Promise<boolean> {
    try {
      // Chamar função do Supabase que verifica se é super admin
      const { data, error } = await supabase.rpc('soft_delete_message', {
        message_id: messageId,
        admin_user_id: adminUserId,
        message_type: messageType
      });

      if (error) {
        console.error('Erro ao fazer soft delete:', error);
        throw new Error(error.message || 'Erro ao excluir mensagem');
      }

      return true;
    } catch (error) {
      console.error('Erro ao fazer soft delete de mensagem:', error);
      throw error;
    }
  },

  /**
   * Restaurar mensagem deletada (apenas super admin)
   * @param messageId ID da mensagem
   * @param adminUserId ID do super admin que está restaurando
   * @param messageType Tipo de mensagem ('regular' ou 'assistant')
   */
  async restoreDeletedMessage(messageId: string, adminUserId: string, messageType: 'regular' | 'assistant' = 'regular'): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('restore_deleted_message', {
        message_id: messageId,
        admin_user_id: adminUserId,
        message_type: messageType
      });

      if (error) {
        console.error('Erro ao restaurar mensagem:', error);
        throw new Error(error.message || 'Erro ao restaurar mensagem');
      }

      return true;
    } catch (error) {
      console.error('Erro ao restaurar mensagem:', error);
      throw error;
    }
  },

  /**
   * Buscar informações de quem deletou uma mensagem
   * @param messageId ID da mensagem
   * @param messageType Tipo de mensagem ('regular' ou 'assistant')
   */
  async getDeletedMessageInfo(messageId: string, messageType: 'regular' | 'assistant' = 'regular'): Promise<{ deletedAt: Date | null, deletedBy: string | null, deletedByName: string | null }> {
    try {
      const table = messageType === 'regular' ? 'messages' : 'assistant_messages';
      
      const { data: message, error } = await supabase
        .from(table)
        .select(`
          deleted_at,
          deleted_by,
          deleted_by_user:users!deleted_by(name, email)
        `)
        .eq('id', messageId)
        .single();

      if (error) {
        throw error;
      }

      return {
        deletedAt: message?.deleted_at ? new Date(message.deleted_at) : null,
        deletedBy: message?.deleted_by || null,
        deletedByName: message?.deleted_by_user?.name || message?.deleted_by_user?.email || null
      };
    } catch (error) {
      console.error('Erro ao buscar info de mensagem deletada:', error);
      return { deletedAt: null, deletedBy: null, deletedByName: null };
    }
  }
}; 